/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './css/RoomDescriptionModal.css';
import {
  firstLetterInUpperCase,
  getHtmlTextItems,
  getWordByQuantity,
} from '../../../Utils/CommonUtils';
import {
  DESCRIPTION,
  SQUARE_METERS,
  ROOM_WORDS,
  BEDROOM_WORDS,
  SLEEPING_PLACES,
  ROOM_VIEW,
  ROOM_COMMENT,
  ROOM_BALCONY,
  ROOM_KITCHEN,
  IN_ROOM,
} from '../../../Utils/Constants/GlobalConstants';
import HotelPhotoSwiper from '../../HotelDetails/HotelPhotoSwiper/HotelPhotoSwiper';
import { ReactComponent as Close } from '../../../images/close-r.svg';
import { ReactComponent as Check } from '../../../images/check.svg';
import { ReactComponent as Dot } from '../../../images/dot.svg';

function RoomDescriptionModal(props) {
  const { isVisible, modalNode, handleModalClick, roomDescription, roomType } =
    props;
  const {
    id,
    name,
    images,
    area,
    roomCount,
    bedroomCount,
    sleepingPlaces,
    location,
    viewDescription,
    description,
    services,
    comment,
    hasBalcony,
    hasKitchen,
  } = roomDescription;

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleEscPress = (e) => {
      if (e.key === 'Escape') {
        handleModalClick();
      }
    };

    document.addEventListener('keydown', handleEscPress, false);

    return () => {
      document.removeEventListener('keydown', handleEscPress, false);
    };
  }, [isVisible, handleModalClick]);

  // HotelPhotoSwiper adds 'https:' itself
  const getSwiperImages = () =>
    images.map((image) => image.replace(/^https?:/, ''));

  const getServices = () => {
    const result = getHtmlTextItems(services);
    const lowerResult = result.map((item) => item.toLowerCase());

    if (hasBalcony && !lowerResult.includes(ROOM_BALCONY.toLowerCase())) {
      result.push(ROOM_BALCONY);
    }
    if (hasKitchen && !lowerResult.includes(ROOM_KITCHEN.toLowerCase())) {
      result.push(ROOM_KITCHEN);
    }

    return result;
  };

  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

  const getViewFact = () => {
    const view = getHtmlTextItems(viewDescription).join(', ');

    if (!view || view.toLowerCase().startsWith(ROOM_VIEW.toLowerCase())) {
      return capitalize(view);
    }

    return `${ROOM_VIEW} ${view}`;
  };

  const getFacts = () =>
    [
      area ? `${area} ${SQUARE_METERS}` : '',
      roomCount
        ? `${roomCount} ${getWordByQuantity(roomCount, ROOM_WORDS)}`
        : '',
      bedroomCount
        ? `${bedroomCount} ${getWordByQuantity(bedroomCount, BEDROOM_WORDS)}`
        : '',
      getViewFact(),
      capitalize(getHtmlTextItems(location).join(', ')),
    ].filter((fact) => fact);

  const renderFacts = () => {
    const facts = getFacts();

    if (facts.length === 0) {
      return null;
    }

    return (
      <div className="rdm_facts">
        {facts.map((fact) => (
          <div key={fact} className="rdm_fact bm_text_18">
            {fact}
          </div>
        ))}
      </div>
    );
  };

  const renderSection = (title, content) => (
    <div className="rdm_section">
      <div className="bm_text_20 mb_12">{title}</div>
      {content}
    </div>
  );

  const renderList = (title, items, Icon, listClass) => {
    if (items.length === 0) {
      return null;
    }

    return renderSection(
      title,
      <div className={listClass}>
        {items.map((item, index) => (
          <div key={index} className="rdm_list_item">
            <Icon className="rdm_list_ico" />
            <span className="br_text_16">{item}</span>
          </div>
        ))}
      </div>,
    );
  };

  const renderText = (title, text) => {
    const items = getHtmlTextItems(text);

    if (items.length === 0) {
      return null;
    }

    return renderSection(
      title,
      items.map((item, index) => (
        <p key={index} className="br_text_16 rdm_text">
          {item}
        </p>
      )),
    );
  };

  return (
    <div className={`rdm_container ${isVisible ? 'rdm_open' : ''}`}>
      <div
        className="rdm_modal"
        ref={(node) => {
          modalNode.current = node;
        }}
      >
        <div className="rdm_title_line">
          <div className="rdm_title">
            {firstLetterInUpperCase(name) || roomType}
          </div>
          <button
            type="button"
            className="rdm_close_but"
            onClick={handleModalClick}
          >
            <Close />
          </button>
        </div>

        {/* remount on open to reset photos and scroll */}
        {isVisible && (
          <div className="rdm_body custom_scroll_container">
            {images && images.length > 0 && (
              <div className="rdm_photo_w">
                <HotelPhotoSwiper key={id} images={getSwiperImages()} />
              </div>
            )}

            {renderFacts()}
            {renderList(
              SLEEPING_PLACES,
              getHtmlTextItems(sleepingPlaces),
              Dot,
              'rdm_list',
            )}
            {renderList(
              IN_ROOM,
              getServices(),
              Check,
              'rdm_list rdm_list_grid',
            )}
            {renderText(DESCRIPTION, description)}
            {renderText(ROOM_COMMENT, comment)}
          </div>
        )}
      </div>
    </div>
  );
}

RoomDescriptionModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  modalNode: PropTypes.instanceOf(Object).isRequired,
  handleModalClick: PropTypes.func.isRequired,
  roomDescription: PropTypes.instanceOf(Object).isRequired,
  roomType: PropTypes.string.isRequired,
};

export default RoomDescriptionModal;
